import { Box, SpaceVertical } from '@looker/components';
import { type FC, memo, useMemo } from 'react';
import { type ICustomFilter, transformStringExpressionToIncludeExclude } from '../../../../../../helpers/helpers';
import useFilterSuggestions from '../../../hooks/useFilterSuggestions';
import { useSubGroupFilterState } from '../../../hooks/useSubGroupFilterState';
import SwitchButtonFilterControl from '../../SwitchButtonFilterControl';
import ClinicalRecency from '../ClinicalRecency';
import { IncludeExcludeListLoader } from '../IncludeExcludeFilter';

interface IToggleFilterWithRecency extends ICustomFilter {
  filterName: string;
  recencyName?: string;
  recencyLabel?: string;
}

const ToggleFilterWithRecency: FC<IToggleFilterWithRecency> = ({
  onChange,
  isAdvancedFilter,
  expression,
  onExpressionTypeChange,
  filterName,
  recencyName,
  recencyLabel,
}) => {
  const resolvedFilterName = isAdvancedFilter ? `${filterName}_advanced` : filterName;

  const { suggestions, isLoading } = useFilterSuggestions(resolvedFilterName);

  const { handleChange } = useSubGroupFilterState(resolvedFilterName, onChange);

  const initialStateForAdvancedFilter = useMemo(() => {
    return expression && isAdvancedFilter ? transformStringExpressionToIncludeExclude(expression) : {};
  }, [expression, isAdvancedFilter]);

  const categories = useMemo(() => suggestions || [], [suggestions]);

  const startingState = useMemo(() => {
    if (categories.length === 0) return {};
    return categories.reduce((state, category) => ({ ...state, [category]: false }), {});
  }, [categories]);

  const mergedStartingState = useMemo(() => {
    return isAdvancedFilter ? { ...startingState, ...initialStateForAdvancedFilter } : startingState;
  }, [isAdvancedFilter, startingState, initialStateForAdvancedFilter]);

  return (
    <>
      {isLoading ? (
        <Box paddingY={'u5'} paddingX={'u12'}>
          <IncludeExcludeListLoader />
        </Box>
      ) : (
        <>
          {recencyName ? (
            <SpaceVertical paddingY={'u4'} paddingX={'u6'}>
              <ClinicalRecency
                onMultiFilterChange={handleChange}
                filterName={recencyName}
                label={recencyLabel || 'Other filters'}
              />
            </SpaceVertical>
          ) : null}
          <SwitchButtonFilterControl
            filterName={resolvedFilterName}
            startingState={mergedStartingState}
            categories={categories}
            onMultiFilterChange={handleChange}
            onExpressionTypeChange={onExpressionTypeChange}
          />
        </>
      )}
    </>
  );
};

export default memo(ToggleFilterWithRecency);
